// Release C-1 · Microsoft 365 sub-page of Communications Settings.
// Route mounted from CommunicationsSettings as a section below SendGrid.
// Owner + Administrator only. Honest six-way provider chip. No token exposure.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { RefreshCcw, Link2, LogOut, Plus, Power } from "lucide-react";

const CHIP = {
  NOT_CONFIGURED:            { bg: "#F1F5F9", fg: "#334155", label: "Not Configured" },
  AUTHORIZATION_REQUIRED:    { bg: "#FEF3C7", fg: "#92400E", label: "Authorization Required" },
  CONFIGURED_DISCONNECTED:   { bg: "#FEE2E2", fg: "#991B1B", label: "Configured but Disconnected" },
  CONNECTED:                 { bg: "#DCFCE7", fg: "#166534", label: "Connected" },
  DEGRADED:                  { bg: "#FED7AA", fg: "#9A3412", label: "Degraded" },
  ERROR:                     { bg: "#FEE2E2", fg: "#991B1B", label: "Error" },
};

function StateChip({ state }) {
  const c = CHIP[state] || CHIP.NOT_CONFIGURED;
  return (
    <span
      data-testid={`m365-chip-${state}`}
      style={{
        display: "inline-block", padding: "3px 12px", borderRadius: 9999,
        background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600,
      }}
    >{c.label}</span>
  );
}

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function MicrosoftCommunications() {
  const [state, setState] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [addingUpn, setAddingUpn] = useState("");
  const [addingKind, setAddingKind] = useState("shared");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/admin/integrations/microsoft365/state"),
        api.get("/admin/integrations/microsoft365/mailboxes"),
      ]);
      setState(s.data);
      setMailboxes(m.data.items || []);
    } catch (e) {
      toast.error("Failed to load Microsoft 365 status");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/admin/integrations/microsoft365/connect", {});
      if (r.data?.authorize_url) {
        // Redirect the top-level window so Entra can set its cookies.
        window.location.href = r.data.authorize_url;
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Cannot start OAuth flow");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Microsoft 365 for your account? Refresh tokens will be tombstoned.")) return;
    setBusy(true);
    try {
      await api.post("/admin/integrations/microsoft365/disconnect", {});
      toast.success("Microsoft 365 disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const healthCheck = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/integrations/microsoft365/health-check", {});
      if (r.data?.ok) toast.success("Health check: CONNECTED");
      else toast.warning(`Health check: ${r.data?.code || "authorization required"}`);
      await load();
    } catch (e) {
      toast.error("Health check failed");
    } finally { setBusy(false); }
  };

  const addMailbox = async () => {
    if (!addingUpn.trim()) { toast.error("UPN required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/integrations/microsoft365/mailboxes", {
        upn: addingUpn.trim(),
        display_name: addingUpn.trim(),
        kind: addingKind,
        retention_years: 10,
      });
      setAddingUpn("");
      toast.success("Mailbox approved");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Failed to add mailbox");
    } finally { setBusy(false); }
  };

  const toggle = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/microsoft365/mailboxes/${id}/toggle`, {});
      const nextState = r?.data?.is_active ? "activated" : "deactivated";
      const upn = r?.data?.mailbox_upn ? ` ${r.data.mailbox_upn}` : "";
      toast.success(`Mailbox${upn} ${nextState}`);
      await load();
    } catch (e) {
      // Surface the EXACT server error so Owner sees actionable detail.
      const d = e?.response?.data?.detail;
      const parts = [];
      if (d?.mailbox_upn) parts.push(d.mailbox_upn);
      if (d?.code) parts.push(`(${d.code})`);
      const suffix = parts.length ? ` — ${parts.join(" ")}` : "";
      toast.error((d?.message || d?.code || "Toggle failed") + suffix);
    } finally { setBusy(false); }
  };

  const subscribe = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/microsoft365/mailboxes/${id}/subscribe`, {});
      const upn = r.data?.mailbox_upn ? ` ${r.data.mailbox_upn}` : "";
      toast.success(`Subscribed${upn} (Graph sub ${(r.data?.graph_subscription_id || "").slice(0, 12)}…)`);
      await load();
    } catch (e) {
      // Surface EXACT server payload — mailbox UPN + code + actionable message.
      const d = e?.response?.data?.detail;
      const parts = [];
      if (d?.mailbox_upn) parts.push(d.mailbox_upn);
      if (d?.code) parts.push(`(${d.code})`);
      const suffix = parts.length ? ` — ${parts.join(" ")}` : "";
      toast.error((d?.message || d?.code || "Subscribe failed") + suffix);
    } finally { setBusy(false); }
  };

  const syncNow = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/microsoft365/mailboxes/${id}/sync-now`, {});
      const upn = r.data?.mailbox_upn ? ` ${r.data.mailbox_upn}` : "";
      toast.success(`Sync${upn} processed ${r.data?.processed ?? 0} · updated ${r.data?.updated ?? 0}`);
      await load();
    } catch (e) {
      // Surface EXACT server payload — mailbox UPN + code + actionable message.
      const d = e?.response?.data?.detail;
      const parts = [];
      if (d?.mailbox_upn) parts.push(d.mailbox_upn);
      if (d?.code) parts.push(`(${d.code})`);
      const suffix = parts.length ? ` — ${parts.join(" ")}` : "";
      toast.error((d?.message || d?.code || "Sync failed") + suffix);
    } finally { setBusy(false); }
  };

  if (!state) return <div style={{ color: "#64748b" }}>Loading Microsoft 365…</div>;
  const providerState = state.provider_state;

  return (
    <div data-testid="m365-communications-section" style={{ marginTop: 24 }}>
      <Card>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Provider</div>
              <div style={{ fontSize: 20, fontWeight: 700, margin: "2px 0 8px" }}>Microsoft 365 · Entra</div>
              <StateChip state={providerState} />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(providerState === "AUTHORIZATION_REQUIRED" || providerState === "CONFIGURED_DISCONNECTED") && (
                <Button data-testid="m365-connect-btn" onClick={connect} disabled={connecting}>
                  <Link2 size={14} style={{ marginRight: 6 }} />
                  {connecting ? "Redirecting…" : "Connect Microsoft 365"}
                </Button>
              )}
              {providerState === "CONNECTED" && (
                <>
                  <Button data-testid="m365-health-btn" variant="outline" onClick={healthCheck} disabled={busy}>
                    <RefreshCcw size={14} style={{ marginRight: 6 }} /> Health check
                  </Button>
                  <Button data-testid="m365-reauth-btn" variant="outline" onClick={connect} disabled={connecting}>
                    Reauthorize
                  </Button>
                  <Button data-testid="m365-disconnect-btn" variant="outline" onClick={disconnect} disabled={busy}>
                    <LogOut size={14} style={{ marginRight: 6 }} /> Disconnect
                  </Button>
                </>
              )}
              {(providerState === "DEGRADED" || providerState === "ERROR") && (
                <Button data-testid="m365-reauth-btn" variant="outline" onClick={connect} disabled={connecting}>
                  Reauthorize
                </Button>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: "#475569" }}>
            {providerState === "NOT_CONFIGURED" && (
              <>Entra app registration hasn't been added yet. See <code>/app/memory/M365_ENTRA_CONFIG_CHECKLIST.md</code> for the exact steps Edwin performs in the Entra portal to move this to <b>AUTHORIZATION REQUIRED</b>.</>
            )}
            {providerState === "AUTHORIZATION_REQUIRED" && (
              <>Provider is configured. Click <b>Connect Microsoft 365</b> to grant consent. Auth Code + PKCE — no secrets leave the backend.</>
            )}
            {providerState === "CONFIGURED_DISCONNECTED" && (
              <>An Owner previously connected and disconnected. Click <b>Connect Microsoft 365</b> to reconnect.</>
            )}
          </div>

          <div style={{ marginTop: 12, fontSize: 11, color: "#64748b" }}>
            <b>Expected scopes:</b>{" "}
            {(state.scopes_expected || []).map((s) => (
              <code key={s} style={{ marginRight: 6 }}>{s}</code>
            ))}
          </div>

          {(state.per_user_states || []).length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Connected owners / admins</div>
              <table style={{ width: "100%", marginTop: 6, borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ textAlign: "left", color: "#64748b" }}>
                  <th style={{ padding: 6 }}>UPN</th>
                  <th style={{ padding: 6 }}>Display name</th>
                  <th style={{ padding: 6 }}>Scopes</th>
                  <th style={{ padding: 6 }}>Last health check</th>
                  <th style={{ padding: 6 }}>State</th>
                </tr></thead>
                <tbody>
                  {state.per_user_states.map((u, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 6 }}>{u.upn || "—"}</td>
                      <td style={{ padding: 6 }}>{u.display_name || "—"}</td>
                      <td style={{ padding: 6, color: "#475569" }}>{(u.scopes || []).length} scopes</td>
                      <td style={{ padding: 6 }}>{fmt(u.last_health_check_at)}</td>
                      <td style={{ padding: 6 }}><StateChip state={u.health_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <CardContent style={{ padding: 20 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Approved mailboxes</div>
          <div style={{ fontSize: 20, fontWeight: 700, margin: "2px 0 8px" }}>Microsoft 365 · Approved Mailboxes</div>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>
            The list of mailboxes ConstructionOS is allowed to read from and send on behalf of.
            Retention defaults to 10 years; edit refused unless Owner + Administrator.
            Per-mailbox chip stays <b>Authorization Required</b> until Release C-2 wires the reachability probe.
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <Input
              data-testid="m365-mailbox-upn"
              placeholder="mailbox@erfenterprisesllc.com"
              value={addingUpn}
              onChange={(e) => setAddingUpn(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            <select
              data-testid="m365-mailbox-kind"
              value={addingKind}
              onChange={(e) => setAddingKind(e.target.value)}
              style={{ padding: 8, borderRadius: 4, border: "1px solid #cbd5e1", fontSize: 13 }}
            >
              <option value="owner_primary">Owner primary</option>
              <option value="shared">Shared mailbox</option>
              <option value="admin">Admin mailbox</option>
              <option value="other">Other</option>
            </select>
            <Button data-testid="m365-mailbox-add" onClick={addMailbox} disabled={busy}>
              <Plus size={14} style={{ marginRight: 6 }} /> Approve mailbox
            </Button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ textAlign: "left", color: "#64748b" }}>
              <th style={{ padding: 6 }}>UPN</th>
              <th style={{ padding: 6 }}>Kind</th>
              <th style={{ padding: 6 }}>Retention</th>
              <th style={{ padding: 6 }}>State</th>
              <th style={{ padding: 6 }}>Active</th>
              <th style={{ padding: 6 }}>Actions</th>
            </tr></thead>
            <tbody>
              {mailboxes.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, color: "#64748b" }}>No approved mailboxes yet.</td></tr>
              )}
              {mailboxes.map((m) => (
                <tr key={m.id} data-testid={`m365-mailbox-row-${m.id}`}
                    style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 6 }}>{m.upn}</td>
                  <td style={{ padding: 6 }}>{m.kind}</td>
                  <td style={{ padding: 6 }}>{m.retention_years} yrs</td>
                  <td style={{ padding: 6 }}><StateChip state={m.chip_state} /></td>
                  <td style={{ padding: 6 }}>{m.is_active ? "Yes" : "No"}</td>
                  <td style={{ padding: 6 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Button size="sm" variant="outline" data-testid={`m365-mailbox-toggle-${m.id}`}
                               onClick={() => toggle(m.id)} disabled={busy}>
                        <Power size={12} style={{ marginRight: 4 }} />
                        {m.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`m365-mailbox-sync-now-${m.id}`}
                               onClick={() => syncNow(m.id)} disabled={busy}>
                        <RefreshCcw size={12} style={{ marginRight: 4 }} /> Sync now
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`m365-mailbox-subscribe-${m.id}`}
                               onClick={() => subscribe(m.id)} disabled={busy}>
                        <Link2 size={12} style={{ marginRight: 4 }} /> Subscribe
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
